// Buying AI credits: Razorpay Checkout in the browser, confirmed by the server (worker/routes/credits.ts).
import type { CreditPack, CreditStatus } from "../../shared/ai-credits";
import { api } from "./api";

export interface CreditStore {
  enabled: boolean;
  packs: CreditPack[];
  keyId: string | null;
}

export const getStore = () => api<CreditStore>("/api/credits/store");

const CHECKOUT_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";

interface RazorpaySuccess {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

interface RazorpayCheckout {
  open(): void;
  on(event: "payment.failed", cb: (r: { error?: { description?: string } }) => void): void;
}
declare global {
  interface Window {
    Razorpay?: new (options: Record<string, unknown>) => RazorpayCheckout;
  }
}

/** Loads Razorpay's Checkout script once. */
export function loadCheckout(): Promise<void> {
  if (window.Razorpay) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = CHECKOUT_SCRIPT;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Couldn't load the payment page. Check your connection and try again."));
    document.head.appendChild(s);
  });
}

export type BuyResult = { ok: true; credits: CreditStatus } | { ok: false; message: string | null };

/** Opens Checkout for `pack` and, once paid, has the server confirm it. message is null when the person just closed it. */
export async function buyPack(pack: CreditPack): Promise<BuyResult> {
  try {
    const order = await api<{ purchaseId: string; orderId: string; amountPaise: number; keyId: string; email: string }>("/api/credits/orders", { method: "POST", json: { packId: pack.id } });
    await loadCheckout();
    // A failed attempt keeps Checkout open so the person can try another way to pay; it only
    // ends when they pay or close it.
    let failed: string | null = null;
    const paid = await new Promise<RazorpaySuccess | null>((resolve) => {
      const checkout = new window.Razorpay!({
        key: order.keyId,
        order_id: order.orderId,
        amount: order.amountPaise,
        currency: "INR",
        name: "ClauseKeep",
        description: `${pack.credits.toLocaleString("en-IN")} AI credits`,
        prefill: { email: order.email },
        theme: { color: "#0f766e" },
        handler: (r: RazorpaySuccess) => resolve(r),
        modal: { ondismiss: () => resolve(null) },
      });
      checkout.on("payment.failed", (r) => {
        failed = r.error?.description ?? "The payment didn't go through.";
      });
      checkout.open();
    });
    if (!paid) return { ok: false, message: failed ? `${failed} You haven't been charged.` : null };
    const done = await api<{ credits: CreditStatus }>(`/api/credits/orders/${order.purchaseId}/verify`, { method: "POST", json: paid });
    return { ok: true, credits: done.credits };
  } catch (err) {
    return { ok: false, message: err instanceof Error ? err.message : "The payment didn't work. Please try again." };
  }
}
